// The session transport client (server plan §4.4): sign-in, refresh, switch, sign-out, forgotten passwords,
// invitations and passkeys, over `/v1/auth/*`. Hand-written like the transport core: these routes are not operations
// (the server sets an httpOnly refresh cookie the page never reads), so nothing is generated for them. Every call
// sends the same-origin cookie and the client header the cookie routes require.
import { z } from 'zod';
import { AUTH_CLIENT_HEADER, InviteCodeSent, InviteSummary, ProfileView, SignedIn, type ProfileUpdate, type RefreshScope } from '@radish/contracts';

export interface AuthClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof fetch;
  /** The client's name in the `x-radish-client` header. */
  readonly client?: string;
  /**
   * Keep the refresh cookie in this client (scripts and tools outside a browser, which has no cookie store of its own).
   * A browser leaves this off: its cookie is httpOnly and the page never sees it.
   */
  readonly keepCookies?: boolean;
}

export interface AuthFailure {
  readonly code: string;
  /** The server's sentence (legacy wording where legacy had one). */
  readonly message: string;
  readonly retryAfterSeconds?: number;
}

export type AuthResult<T> = { readonly ok: true; readonly data: T } | { readonly ok: false; readonly status: number; readonly error: AuthFailure };

const Failure = z.object({ message: z.string(), error: z.object({ code: z.string(), details: z.object({ retryAfterSeconds: z.number().optional() }).optional() }) });
const Envelope = z.object({ success: z.literal(true), data: z.unknown() });

export interface AuthClient {
  signIn(input: { email: string; password: string; rememberMe: boolean }): Promise<AuthResult<SignedIn>>;
  /**
   * A new access token from the refresh cookie (a reload, or an access token about to expire). `scope`: the account and
   * location this tab acts at (contracts RefreshScope); the token is minted there, or the server answers 403
   * `scope_lost`. Without it, the session's own context (a reload, before the tab knows where it acts).
   */
  refresh(scope?: RefreshScope): Promise<AuthResult<SignedIn>>;
  /** `remember`: a pick in the location switcher, which a merchant's next sign-in starts at (EP-AUTH-05, B1727). */
  switchTo(input: { accountId: string; locationId: string | null; remember?: boolean }): Promise<AuthResult<SignedIn>>;

  /** Proves it is still this person (password), renewing the session with its step-up time (Commander's Alert). */
  stepUp(bearer: string, input: { password: string }): Promise<AuthResult<SignedIn>>;
  signOut(): Promise<AuthResult<{ signedOut: boolean }>>;
  forgotPassword(email: string): Promise<AuthResult<{ sent: boolean }>>;
  checkResetToken(token: string): Promise<AuthResult<{ valid: boolean }>>;
  resetPassword(input: { token: string; password: string }): Promise<AuthResult<{ reset: boolean }>>;
  checkInvite(token: string): Promise<AuthResult<{ invite: InviteSummary | null }>>;
  /** A link invite's code, mailed to the invite's own address (A191). */
  sendInviteCode(token: string): Promise<AuthResult<{ sent: true }>>;
  acceptInvite(input: { token: string; name?: string; phone?: string; password?: string; code?: string }): Promise<AuthResult<{ accepted: boolean; existingUser: boolean }>>;
  passkeySignInOptions(): Promise<AuthResult<{ options: Record<string, unknown> }>>;
  passkeySignIn(input: { response: unknown; rememberMe: boolean }): Promise<AuthResult<SignedIn>>;
  passkeyRegistrationOptions(accessToken: string): Promise<AuthResult<{ options: Record<string, unknown> }>>;
  registerPasskey(accessToken: string, input: { response: unknown; label?: string }): Promise<AuthResult<{ registered: boolean }>>;
  /**
   * A merchant's menu review link (ops plan 2026-10-14-staff-agents): the secret from the link's fragment for a short
   * `review_link` token for that one store and preview. The secret travels only in this body, never in a URL.
   */
  reviewLinkSession(secret: string): Promise<AuthResult<ReviewLinkSession>>;
  /**
   * A Launchpad share link (ops plan 2026-10-17-launchpad-share): the secret from the link's fragment for a short token
   * for that one share. The secret travels only in this body, never in a URL.
   */
  shareLinkSession(secret: string): Promise<AuthResult<ShareLinkSession>>;
  /** SCR-030 Profile: the signed-in user's own details. */
  profile(accessToken: string): Promise<AuthResult<{ profile: ProfileView }>>;
  updateProfile(accessToken: string, input: ProfileUpdate): Promise<AuthResult<{ profile: ProfileView }>>;
  /** Profile's Reset Password: ends the user's other sessions (the refresh cookie names the one kept). */
  changePassword(accessToken: string, input: { currentPassword: string; password: string }): Promise<AuthResult<{ changed: boolean }>>;
}

const Options = z.object({ options: z.record(z.string(), z.unknown()) });

/** A Radish Broker access token exchanged from the session: 15 minutes, for one endpoint (expiresAt in epoch ms). */
const BrokerToken = z.object({ accessToken: z.string(), expiresAt: z.number(), resource: z.enum(['onboarding', 'support']) });
export type BrokerToken = z.infer<typeof BrokerToken>;

const ReviewLinkSession = z.object({ accessToken: z.string(), /** Epoch ms. */ expiresAt: z.number(), linkId: z.string(), locationId: z.string(), previewId: z.string() });
export type ReviewLinkSession = z.infer<typeof ReviewLinkSession>;

const ShareLinkSession = z.object({ accessToken: z.string(), /** Epoch ms. */ expiresAt: z.number(), shareId: z.string(), locationId: z.string() });
export type ShareLinkSession = z.infer<typeof ShareLinkSession>;

/** Applies `set-cookie` answers to a jar: a value is stored, an emptied or expired one (`Max-Age=0`) removed. */
function keep(jar: Map<string, string>, setCookies: readonly string[]): void {
  for (const header of setCookies) {
    const [pair = '', ...attributes] = header.split(';');
    const at = pair.indexOf('=');
    if (at <= 0) continue;
    const name = pair.slice(0, at).trim();
    const value = pair.slice(at + 1).trim();
    if (value === '' || attributes.some((a) => a.trim().toLowerCase() === 'max-age=0')) jar.delete(name);
    else jar.set(name, value);
  }
}

/**
 * The staff apps' auth client (Launchpad and Commander only; ops plan 2026-10-18-staff-single-sign-in): the session
 * transport plus `brokerToken`, the one sign-in's short-lived Radish Broker token for the app's own endpoint, with no
 * browser and no second password. The cookie is not rotated. Other apps build on `createAuthClient` and never see it.
 */
export interface StaffAuthClient extends AuthClient {
  /** `locationId` pins the token to one location (Launchpad's case): the Broker refuses any tool call on another. */
  brokerToken(resource: 'onboarding' | 'support', locationId?: string): Promise<AuthResult<BrokerToken>>;
}

export function createStaffAuthClient(o: AuthClientOptions): StaffAuthClient {
  return createAuthClientWith(o, (send) => ({ brokerToken: (resource, locationId) => send('broker-token', locationId === undefined ? { resource } : { resource, locationId }, BrokerToken) }));
}

export function createAuthClient(o: AuthClientOptions): AuthClient {
  return createAuthClientWith(o, () => ({}));
}

type Send = <T>(path: string, body: unknown, schema: z.ZodType<T>, bearer?: string) => Promise<AuthResult<T>>;

function createAuthClientWith<X extends object>(o: AuthClientOptions, more: (send: Send) => X): AuthClient & X {
  const jar = new Map<string, string>();
  const send = async <T>(path: string, body: unknown, schema: z.ZodType<T>, bearer?: string): Promise<AuthResult<T>> => {
    let res: Response;
    try {
      const doFetch = o.fetch ?? fetch;
      const cookie = [...jar].map(([name, value]) => `${name}=${value}`).join('; ');
      res = await doFetch(`${o.baseUrl}/v1/auth/${path}`, {
        method: 'POST', credentials: 'same-origin', body: JSON.stringify(body),
        headers: {
          'content-type': 'application/json', [AUTH_CLIENT_HEADER]: o.client ?? 'backoffice',
          ...(bearer === undefined ? {} : { authorization: `Bearer ${bearer}` }), ...(o.keepCookies === true && cookie !== '' ? { cookie } : {}),
        },
      });
    } catch {
      return { ok: false, status: 0, error: { code: 'network_error', message: 'The server could not be reached.' } };
    }
    if (o.keepCookies === true) keep(jar, res.headers.getSetCookie());
    const json: unknown = await res.json().catch(() => undefined);
    if (res.ok) {
      const envelope = Envelope.safeParse(json);
      const data = envelope.success ? schema.safeParse(envelope.data.data) : null;
      if (data?.success === true) return { ok: true, data: data.data };
    } else {
      const failure = Failure.safeParse(json);
      if (failure.success) {
        const retry = failure.data.error.details?.retryAfterSeconds;
        return { ok: false, status: res.status, error: { code: failure.data.error.code, message: failure.data.message, ...(retry === undefined ? {} : { retryAfterSeconds: retry }) } };
      }
    }
    return { ok: false, status: res.status, error: { code: 'unexpected_response', message: 'Unexpected response from the server.' } };
  };
  return {
    signIn: (input) => send('sign-in', input, SignedIn),
    refresh: (scope) => send('refresh', scope ?? {}, SignedIn),
    switchTo: (input) => send('switch', input, SignedIn),
    stepUp: (bearer, input) => send('step-up', input, SignedIn, bearer),
    signOut: () => send('sign-out', {}, z.object({ signedOut: z.boolean() })),
    forgotPassword: (email) => send('password/forgot', { email }, z.object({ sent: z.boolean() })),
    reviewLinkSession: (secret) => send('review-link/session', { secret }, ReviewLinkSession),
    shareLinkSession: (secret) => send('share/session', { secret }, ShareLinkSession),
    checkResetToken: (token) => send('password/reset/check', { token }, z.object({ valid: z.boolean() })),
    resetPassword: (input) => send('password/reset', input, z.object({ reset: z.boolean() })),
    checkInvite: (token) => send('invites/check', { token }, z.object({ invite: InviteSummary.nullable() })),
    sendInviteCode: (token) => send('invites/code', { token }, InviteCodeSent),
    acceptInvite: (input) => send('invites/accept', input, z.object({ accepted: z.boolean(), existingUser: z.boolean() })),
    passkeySignInOptions: () => send('passkeys/sign-in/options', {}, Options),
    passkeySignIn: (input) => send('passkeys/sign-in', input, SignedIn),
    passkeyRegistrationOptions: (token) => send('passkeys/register/options', {}, Options, token),
    registerPasskey: (token, input) => send('passkeys/register', input, z.object({ registered: z.boolean() }), token),
    profile: (token) => send('profile', {}, z.object({ profile: ProfileView }), token),
    updateProfile: (token, input) => send('profile/update', input, z.object({ profile: ProfileView }), token),
    changePassword: (token, input) => send('password/change', input, z.object({ changed: z.boolean() }), token),
    ...more(send),
  };
}
